/**
 * Universal Pure-Vector SVG Instrument Icon Generator
 * 
 * Provides crisp, high-contrast vector badges and dual-disc currency flags
 * for all 83 OTC instruments (Forex, Crypto, Commodities, Stocks, Indices).
 * 
 * ZERO dependency on OS-level emoji fonts, Unicode regional indicators, or CDNs.
 * 100% compatible across Windows 10/11, macOS, Android WebViews, iOS, and Linux.
 */

export type VisualCategory = 'forex' | 'crypto' | 'commodity' | 'stock' | 'index';

// High-contrast vector flag graphics centered at (0, 0) with radius 12 (bounds -12 to 12)
const CURRENCY_VECTORS: Record<string, string> = {
  USD: `
    <rect x="-12" y="-12" width="24" height="24" fill="#b22234"/>
    <rect x="-12" y="-9.5" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="-5.9" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="-2.3" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="1.3" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="4.9" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="8.5" width="24" height="1.8" fill="#ffffff"/>
    <rect x="-12" y="-12" width="11" height="13.2" fill="#1e2d5a"/>
    <circle cx="-9" cy="-9" r="0.9" fill="#ffffff"/>
    <circle cx="-5.5" cy="-9" r="0.9" fill="#ffffff"/>
    <circle cx="-7.2" cy="-6" r="0.9" fill="#ffffff"/>
    <circle cx="-9" cy="-3" r="0.9" fill="#ffffff"/>
    <circle cx="-5.5" cy="-3" r="0.9" fill="#ffffff"/>
  `,
  EUR: `
    <rect x="-12" y="-12" width="24" height="24" fill="#003399"/>
    <circle cx="0" cy="-7.5" r="0.85" fill="#ffcc00"/>
    <circle cx="3.75" cy="-6.5" r="0.85" fill="#ffcc00"/>
    <circle cx="6.5" cy="-3.75" r="0.85" fill="#ffcc00"/>
    <circle cx="7.5" cy="0" r="0.85" fill="#ffcc00"/>
    <circle cx="6.5" cy="3.75" r="0.85" fill="#ffcc00"/>
    <circle cx="3.75" cy="6.5" r="0.85" fill="#ffcc00"/>
    <circle cx="0" cy="7.5" r="0.85" fill="#ffcc00"/>
    <circle cx="-3.75" cy="6.5" r="0.85" fill="#ffcc00"/>
    <circle cx="-6.5" cy="3.75" r="0.85" fill="#ffcc00"/>
    <circle cx="-7.5" cy="0" r="0.85" fill="#ffcc00"/>
    <circle cx="-6.5" cy="-3.75" r="0.85" fill="#ffcc00"/>
    <circle cx="-3.75" cy="-6.5" r="0.85" fill="#ffcc00"/>
  `,
  GBP: `
    <rect x="-12" y="-12" width="24" height="24" fill="#012169"/>
    <path d="M-12 -12 L12 12 M-12 12 L12 -12" stroke="#ffffff" stroke-width="4.5"/>
    <path d="M-12 -12 L12 12 M-12 12 L12 -12" stroke="#c8102e" stroke-width="2.2"/>
    <path d="M-12 0 L12 0 M0 -12 L0 12" stroke="#ffffff" stroke-width="6.5"/>
    <path d="M-12 0 L12 0 M0 -12 L0 12" stroke="#c8102e" stroke-width="3.5"/>
  `,
  JPY: `
    <rect x="-12" y="-12" width="24" height="24" fill="#ffffff"/>
    <circle cx="0" cy="0" r="5.5" fill="#bc002d"/>
  `,
  AUD: `
    <rect x="-12" y="-12" width="24" height="24" fill="#00008b"/>
    <path d="M-12 -12 L0 0 M-12 0 L0 -12" stroke="#ffffff" stroke-width="2"/>
    <path d="M-12 -12 L0 0 M-12 0 L0 -12" stroke="#c8102e" stroke-width="1"/>
    <path d="M-12 -6 L0 -6 M-6 -12 L-6 0" stroke="#ffffff" stroke-width="3"/>
    <path d="M-12 -6 L0 -6 M-6 -12 L-6 0" stroke="#c8102e" stroke-width="1.6"/>
    <polygon points="-6,4 -5.4,5.4 -4,5.4 -5,6.2 -4.6,7.6 -6,6.8 -7.4,7.6 -7,6.2 -8,5.4 -6.6,5.4" fill="#ffffff"/>
    <circle cx="6" cy="-4" r="1.1" fill="#ffffff"/>
    <circle cx="7.5" cy="1" r="1.1" fill="#ffffff"/>
    <circle cx="4" cy="6" r="1.1" fill="#ffffff"/>
    <circle cx="2" cy="0" r="1.1" fill="#ffffff"/>
    <circle cx="4.8" cy="1.5" r="0.7" fill="#ffffff"/>
  `,
  CAD: `
    <rect x="-12" y="-12" width="6" height="24" fill="#ff0000"/>
    <rect x="-6" y="-12" width="12" height="24" fill="#ffffff"/>
    <rect x="6" y="-12" width="6" height="24" fill="#ff0000"/>
    <path d="M0 -6 L1.3 -3.5 L3.5 -4.5 L2.5 -1.8 L5 -1 L3.5 1.2 L4 3.2 L1.5 2.5 L1 5.2 L0 4.5 L-1 5.2 L-1.5 2.5 L-4 3.2 L-3.5 1.2 L-5 -1 L-2.5 -1.8 L-3.5 -4.5 L-1.3 -3.5 Z" fill="#ff0000"/>
    <rect x="-0.4" y="4" width="0.8" height="2" fill="#ff0000"/>
  `,
  CHF: `
    <rect x="-12" y="-12" width="24" height="24" fill="#d52b1e"/>
    <rect x="-2.2" y="-6.5" width="4.4" height="13" fill="#ffffff" rx="0.5"/>
    <rect x="-6.5" y="-2.2" width="13" height="4.4" fill="#ffffff" rx="0.5"/>
  `,
  NZD: `
    <rect x="-12" y="-12" width="24" height="24" fill="#00247d"/>
    <path d="M-12 -12 L0 0 M-12 0 L0 -12" stroke="#ffffff" stroke-width="2"/>
    <path d="M-12 -12 L0 0 M-12 0 L0 -12" stroke="#c8102e" stroke-width="1"/>
    <path d="M-12 -6 L0 -6 M-6 -12 L-6 0" stroke="#ffffff" stroke-width="3"/>
    <path d="M-12 -6 L0 -6 M-6 -12 L-6 0" stroke="#c8102e" stroke-width="1.6"/>
    <circle cx="5" cy="-4.5" r="1.3" fill="#cc142b" stroke="#ffffff" stroke-width="0.5"/>
    <circle cx="7.5" cy="0.5" r="1.3" fill="#cc142b" stroke="#ffffff" stroke-width="0.5"/>
    <circle cx="4" cy="5.5" r="1.3" fill="#cc142b" stroke="#ffffff" stroke-width="0.5"/>
    <circle cx="2" cy="0.5" r="1.1" fill="#cc142b" stroke="#ffffff" stroke-width="0.5"/>
  `,
  INR: `
    <rect x="-12" y="-12" width="24" height="8" fill="#ff9933"/>
    <rect x="-12" y="-4" width="24" height="8" fill="#ffffff"/>
    <rect x="-12" y="4" width="24" height="8" fill="#138808"/>
    <circle cx="0" cy="0" r="3" fill="none" stroke="#000080" stroke-width="0.8"/>
    <circle cx="0" cy="0" r="0.8" fill="#000080"/>
    <path d="M-2.8 0 L2.8 0 M0 -2.8 L0 2.8 M-2 -2 L2 2 M-2 2 L2 -2" stroke="#000080" stroke-width="0.5"/>
  `,
  PKR: `
    <rect x="-12" y="-12" width="6" height="24" fill="#ffffff"/>
    <rect x="-6" y="-12" width="18" height="24" fill="#01411c"/>
    <circle cx="3" cy="0" r="4.5" fill="#ffffff"/>
    <circle cx="4.3" cy="-0.9" r="4" fill="#01411c"/>
    <polygon points="5.5,-3.2 4.9,-2.3 5.8,-1.9 4.8,-1.8 4.7,-0.7 4.1,-1.6 3.1,-1.4 3.7,-2.3 3.3,-3.2 4.3,-2.8" fill="#ffffff"/>
  `,
  BRL: `
    <rect x="-12" y="-12" width="24" height="24" fill="#009b3a"/>
    <polygon points="0,-8.5 9.5,0 0,8.5 -9.5,0" fill="#fedf01"/>
    <circle cx="0" cy="0" r="4.5" fill="#002776"/>
    <path d="M-4 -1 Q0 1.8 4 -0.5" stroke="#ffffff" stroke-width="0.9" fill="none"/>
  `,
  MXN: `
    <rect x="-12" y="-12" width="8" height="24" fill="#006847"/>
    <rect x="-4" y="-12" width="8" height="24" fill="#ffffff"/>
    <rect x="4" y="-12" width="8" height="24" fill="#ce1126"/>
    <circle cx="0" cy="0" r="2.8" fill="#8c6239"/>
    <circle cx="0" cy="0" r="1.6" fill="#e5b324"/>
  `,
  TRY: `
    <rect x="-12" y="-12" width="24" height="24" fill="#e30a17"/>
    <circle cx="-1.5" cy="0" r="5" fill="#ffffff"/>
    <circle cx="-0.2" cy="0" r="4" fill="#e30a17"/>
    <polygon points="4.5,0 3.2,0.8 3.7,-0.5 2.6,-1.2 3.9,-1.2 4.5,-2.4 5,-1.2 6.3,-1.2 5.2,-0.5 5.7,0.8" fill="#ffffff"/>
  `,
  ZAR: `
    <rect x="-12" y="-12" width="24" height="12" fill="#e03c31"/>
    <rect x="-12" y="0" width="24" height="12" fill="#001489"/>
    <path d="M-12 -12 L-3 0 L-12 12 Z" fill="#000000"/>
    <path d="M-12 -12 L-1 0 L-12 12 M-1 0 L12 0" stroke="#ffb81c" stroke-width="3" fill="none"/>
    <path d="M-12 -12 L0 0 L-12 12 M0 0 L12 0" stroke="#007749" stroke-width="2.2" fill="none"/>
  `,
  ARS: `
    <rect x="-12" y="-12" width="24" height="7.5" fill="#74acdf"/>
    <rect x="-12" y="-4.5" width="24" height="9" fill="#ffffff"/>
    <rect x="-12" y="4.5" width="24" height="7.5" fill="#74acdf"/>
    <circle cx="0" cy="0" r="2.5" fill="#f6b40e"/>
    <circle cx="0" cy="0" r="1.3" fill="#b07000"/>
  `,
  BDT: `
    <rect x="-12" y="-12" width="24" height="24" fill="#006a4e"/>
    <circle cx="-1.5" cy="0" r="5.5" fill="#f42a41"/>
  `,
  COP: `
    <rect x="-12" y="-12" width="24" height="12" fill="#fcd116"/>
    <rect x="-12" y="0" width="24" height="6" fill="#003893"/>
    <rect x="-12" y="6" width="24" height="6" fill="#ce1126"/>
  `,
  DZD: `
    <rect x="-12" y="-12" width="12" height="24" fill="#006233"/>
    <rect x="0" y="-12" width="12" height="24" fill="#ffffff"/>
    <circle cx="0" cy="0" r="4.6" fill="#d21034"/>
    <circle cx="1.2" cy="0" r="3.7" fill="#ffffff"/>
    <polygon points="1.8,0 0.8,0.7 1.2,-0.5 0.3,-1.2 1.4,-1.2 1.8,-2.3 2.3,-1.2 3.5,-1.2 2.6,-0.5 3,0.7" fill="#d21034"/>
  `,
  EGP: `
    <rect x="-12" y="-12" width="24" height="8" fill="#ce1126"/>
    <rect x="-12" y="-4" width="24" height="8" fill="#ffffff"/>
    <rect x="-12" y="4" width="24" height="8" fill="#000000"/>
    <circle cx="0" cy="0" r="2.2" fill="#c09300"/>
  `,
  IDR: `
    <rect x="-12" y="-12" width="24" height="12" fill="#ff0000"/>
    <rect x="-12" y="0" width="24" height="12" fill="#ffffff"/>
  `,
  NGN: `
    <rect x="-12" y="-12" width="8" height="24" fill="#008751"/>
    <rect x="-4" y="-12" width="8" height="24" fill="#ffffff"/>
    <rect x="4" y="-12" width="8" height="24" fill="#008751"/>
  `,
  PHP: `
    <rect x="-12" y="-12" width="24" height="12" fill="#0038a8"/>
    <rect x="-12" y="0" width="24" height="12" fill="#ce1126"/>
    <polygon points="-12,-12 -2,0 -12,12" fill="#ffffff"/>
    <circle cx="-7.5" cy="0" r="1.8" fill="#fcd116"/>
  `,
  SGD: `
    <rect x="-12" y="-12" width="24" height="12" fill="#ed2939"/>
    <rect x="-12" y="0" width="24" height="12" fill="#ffffff"/>
    <circle cx="-6.5" cy="-6" r="3" fill="#ffffff"/>
    <circle cx="-5.3" cy="-6" r="2.5" fill="#ed2939"/>
    <circle cx="-4" cy="-6" r="0.7" fill="#ffffff"/>
  `,
  CNY: `
    <rect x="-12" y="-12" width="24" height="24" fill="#de2910"/>
    <polygon points="-7,-7 -5.5,-7 -5,-8.5 -4.5,-7 -3,-7 -4.2,-6 -3.8,-4.5 -5,-5.5 -6.2,-4.5 -5.8,-6" fill="#ffde00"/>
    <circle cx="-1.5" cy="-8.5" r="0.7" fill="#ffde00"/>
    <circle cx="0" cy="-6.5" r="0.7" fill="#ffde00"/>
    <circle cx="0" cy="-4" r="0.7" fill="#ffde00"/>
    <circle cx="-1.5" cy="-2" r="0.7" fill="#ffde00"/>
  `,
  HKD: `
    <rect x="-12" y="-12" width="24" height="24" fill="#de2910"/>
    <circle cx="0" cy="0" r="1.5" fill="#ffffff"/>
    <circle cx="0" cy="-3.5" r="1.8" fill="#ffffff"/>
    <circle cx="3.3" cy="-1.1" r="1.8" fill="#ffffff"/>
    <circle cx="2.1" cy="2.8" r="1.8" fill="#ffffff"/>
    <circle cx="-2.1" cy="2.8" r="1.8" fill="#ffffff"/>
    <circle cx="-3.3" cy="-1.1" r="1.8" fill="#ffffff"/>
    <circle cx="0" cy="0" r="1" fill="#de2910"/>
  `,
  SAR: `
    <rect x="-12" y="-12" width="24" height="24" fill="#165d31"/>
    <path d="M-8 -3 L8 -3 M-6 -1 L6 -1" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M-7 3 L7 3 M-7 1.5 L-7 4.5 M-4 3 L-4 4.5" stroke="#ffffff" stroke-width="0.9"/>
  `,
  AED: `
    <rect x="-6" y="-12" width="18" height="8" fill="#00732f"/>
    <rect x="-6" y="-4" width="18" height="8" fill="#ffffff"/>
    <rect x="-6" y="4" width="18" height="8" fill="#000000"/>
    <rect x="-12" y="-12" width="6" height="24" fill="#ff0000"/>
  `,
  THB: `
    <rect x="-12" y="-12" width="24" height="4" fill="#a51931"/>
    <rect x="-12" y="-8" width="24" height="4" fill="#ffffff"/>
    <rect x="-12" y="-4" width="24" height="8" fill="#2d2a4a"/>
    <rect x="-12" y="4" width="24" height="4" fill="#ffffff"/>
    <rect x="-12" y="8" width="24" height="4" fill="#a51931"/>
  `,
  KRW: `
    <rect x="-12" y="-12" width="24" height="24" fill="#ffffff"/>
    <path d="M0 -4.5 A4.5 4.5 0 0 1 0 4.5 A2.25 2.25 0 0 1 0 0 A2.25 2.25 0 0 0 0 -4.5 Z" fill="#cd2e3a"/>
    <path d="M0 4.5 A4.5 4.5 0 0 1 0 -4.5 A2.25 2.25 0 0 1 0 0 A2.25 2.25 0 0 0 0 4.5 Z" fill="#0047a0"/>
  `,
  PLN: `
    <rect x="-12" y="-12" width="24" height="12" fill="#ffffff"/>
    <rect x="-12" y="0" width="24" height="12" fill="#dc143c"/>
  `,
  SEK: `
    <rect x="-12" y="-12" width="24" height="24" fill="#006aa7"/>
    <rect x="-4" y="-12" width="4.5" height="24" fill="#fecc00"/>
    <rect x="-12" y="-2.2" width="24" height="4.5" fill="#fecc00"/>
  `,
  NOK: `
    <rect x="-12" y="-12" width="24" height="24" fill="#ba0c2f"/>
    <rect x="-5" y="-12" width="6" height="24" fill="#ffffff"/>
    <rect x="-12" y="-3" width="24" height="6" fill="#ffffff"/>
    <rect x="-4" y="-12" width="4" height="24" fill="#00205b"/>
    <rect x="-12" y="-2" width="24" height="4" fill="#00205b"/>
  `,
  DKK: `
    <rect x="-12" y="-12" width="24" height="24" fill="#c60c30"/>
    <rect x="-4" y="-12" width="4.5" height="24" fill="#ffffff"/>
    <rect x="-12" y="-2.2" width="24" height="4.5" fill="#ffffff"/>
  `,
};

// Fallback currency vector disc for any unrecognized 3-letter currency code
function getFallbackCurrencyVector(code: string): string {
  const safeCode = (code || 'FX').slice(0, 3).toUpperCase();
  return `
    <rect x="-12" y="-12" width="24" height="24" fill="#1e293b"/>
    <text x="0" y="3.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="9" fill="#38bdf8">${safeCode}</text>
  `;
}

// Single-asset pure vector SVG definitions (viewBox 0 0 32 32, center cx=16, cy=16, r=15)
const SINGLE_ASSET_SVGS: Record<string, string> = {
  // Cryptocurrencies
  BTC: `
    <circle cx="16" cy="16" r="14.5" fill="#f7931a" stroke="#ffd166" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">₿</text>
  `,
  BTCUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#f7931a" stroke="#ffd166" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">₿</text>
  `,
  ETH: `
    <circle cx="16" cy="16" r="14.5" fill="#627eea" stroke="#a5b4fc" stroke-width="1"/>
    <polygon points="16,6 10.5,15.5 16,12.5 21.5,15.5" fill="#ffffff" fill-opacity="0.95"/>
    <polygon points="16,6 10.5,15.5 16,12.5" fill="#e0e7ff"/>
    <polygon points="16,17.5 10.5,20 16,26 21.5,20" fill="#ffffff" fill-opacity="0.95"/>
    <polygon points="16,17.5 10.5,20 16,26" fill="#c7d2fe"/>
  `,
  ETHUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#627eea" stroke="#a5b4fc" stroke-width="1"/>
    <polygon points="16,6 10.5,15.5 16,12.5 21.5,15.5" fill="#ffffff" fill-opacity="0.95"/>
    <polygon points="16,6 10.5,15.5 16,12.5" fill="#e0e7ff"/>
    <polygon points="16,17.5 10.5,20 16,26 21.5,20" fill="#ffffff" fill-opacity="0.95"/>
    <polygon points="16,17.5 10.5,20 16,26" fill="#c7d2fe"/>
  `,
  SOL: `
    <circle cx="16" cy="16" r="14.5" fill="#14121f" stroke="#a855f7" stroke-width="1"/>
    <defs>
      <linearGradient id="solG" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stop-color="#00FFA3"/>
        <stop offset="100%" stop-color="#DC1FFF"/>
      </linearGradient>
    </defs>
    <path d="M9.5 10.5 L19.5 10.5 L22.5 12.5 L12.5 12.5 Z" fill="url(#solG)"/>
    <path d="M22.5 15 L12.5 15 L9.5 17 L19.5 17 Z" fill="url(#solG)"/>
    <path d="M9.5 19.5 L19.5 19.5 L22.5 21.5 L12.5 21.5 Z" fill="url(#solG)"/>
  `,
  SOLUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#14121f" stroke="#a855f7" stroke-width="1"/>
    <defs>
      <linearGradient id="solG2" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stop-color="#00FFA3"/>
        <stop offset="100%" stop-color="#DC1FFF"/>
      </linearGradient>
    </defs>
    <path d="M9.5 10.5 L19.5 10.5 L22.5 12.5 L12.5 12.5 Z" fill="url(#solG2)"/>
    <path d="M22.5 15 L12.5 15 L9.5 17 L19.5 17 Z" fill="url(#solG2)"/>
    <path d="M9.5 19.5 L19.5 19.5 L22.5 21.5 L12.5 21.5 Z" fill="url(#solG2)"/>
  `,
  XRP: `
    <circle cx="16" cy="16" r="14.5" fill="#18202c" stroke="#94a3b8" stroke-width="1"/>
    <path d="M9 10 L12.2 10 L16 14.2 L19.8 10 L23 10 L17.6 16 L23 22 L19.8 22 L16 17.8 L12.2 22 L9 22 L14.4 16 Z" fill="#ffffff"/>
  `,
  XRPUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#18202c" stroke="#94a3b8" stroke-width="1"/>
    <path d="M9 10 L12.2 10 L16 14.2 L19.8 10 L23 10 L17.6 16 L23 22 L19.8 22 L16 17.8 L12.2 22 L9 22 L14.4 16 Z" fill="#ffffff"/>
  `,
  TON: `
    <circle cx="16" cy="16" r="14.5" fill="#0088cc" stroke="#38bdf8" stroke-width="1"/>
    <polygon points="16,7 23.5,13 16,25 8.5,13" fill="none" stroke="#ffffff" stroke-width="1.8" stroke-linejoin="round"/>
    <line x1="8.5" y1="13" x2="23.5" y2="13" stroke="#ffffff" stroke-width="1.5"/>
    <line x1="16" y1="7" x2="16" y2="25" stroke="#ffffff" stroke-width="1.5"/>
    <polygon points="16,13 8.5,13 16,7" fill="#ffffff" fill-opacity="0.3"/>
  `,
  TONUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#0088cc" stroke="#38bdf8" stroke-width="1"/>
    <polygon points="16,7 23.5,13 16,25 8.5,13" fill="none" stroke="#ffffff" stroke-width="1.8" stroke-linejoin="round"/>
    <line x1="8.5" y1="13" x2="23.5" y2="13" stroke="#ffffff" stroke-width="1.5"/>
    <line x1="16" y1="7" x2="16" y2="25" stroke="#ffffff" stroke-width="1.5"/>
    <polygon points="16,13 8.5,13 16,7" fill="#ffffff" fill-opacity="0.3"/>
  `,
  DOGE: `
    <circle cx="16" cy="16" r="14.5" fill="#c2a633" stroke="#fef08a" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">Ð</text>
  `,
  DOGEUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#c2a633" stroke="#fef08a" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">Ð</text>
  `,
  BNB: `
    <circle cx="16" cy="16" r="14.5" fill="#181a20" stroke="#f0b90b" stroke-width="1.2"/>
    <polygon points="16,8 20,12 16,16 12,12" fill="#f0b90b"/>
    <polygon points="16,17 19.5,20.5 16,24 12.5,20.5" fill="#f0b90b"/>
    <polygon points="9.5,14 12,16.5 9.5,19 7,16.5" fill="#f0b90b"/>
    <polygon points="22.5,14 25,16.5 22.5,19 20,16.5" fill="#f0b90b"/>
  `,
  BNBUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#181a20" stroke="#f0b90b" stroke-width="1.2"/>
    <polygon points="16,8 20,12 16,16 12,12" fill="#f0b90b"/>
    <polygon points="16,17 19.5,20.5 16,24 12.5,20.5" fill="#f0b90b"/>
    <polygon points="9.5,14 12,16.5 9.5,19 7,16.5" fill="#f0b90b"/>
    <polygon points="22.5,14 25,16.5 22.5,19 20,16.5" fill="#f0b90b"/>
  `,
  LTC: `
    <circle cx="16" cy="16" r="14.5" fill="#345d9d" stroke="#93c5fd" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">Ł</text>
  `,
  LTCUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#345d9d" stroke="#93c5fd" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">Ł</text>
  `,
  BCH: `
    <circle cx="16" cy="16" r="14.5" fill="#0ac18e" stroke="#6ee7b7" stroke-width="1"/>
    <g transform="rotate(-15 16 16)"><text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">₿</text></g>
  `,
  BCHUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#0ac18e" stroke="#6ee7b7" stroke-width="1"/>
    <g transform="rotate(-15 16 16)"><text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#ffffff">₿</text></g>
  `,
  ADA: `
    <circle cx="16" cy="16" r="14.5" fill="#0033ad" stroke="#60a5fa" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="15" fill="#ffffff">₳</text>
  `,
  ADAUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#0033ad" stroke="#60a5fa" stroke-width="1"/>
    <text x="16" y="21.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="15" fill="#ffffff">₳</text>
  `,
  DOT: `
    <circle cx="16" cy="16" r="14.5" fill="#e6007a" stroke="#f472b6" stroke-width="1"/>
    <circle cx="16" cy="16" r="6" fill="#ffffff"/>
    <circle cx="16" cy="7" r="2.2" fill="#ffffff"/>
    <circle cx="16" cy="25" r="2.2" fill="#ffffff"/>
    <circle cx="24" cy="12" r="2.2" fill="#ffffff"/>
    <circle cx="8" cy="20" r="2.2" fill="#ffffff"/>
  `,
  DOTUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#e6007a" stroke="#f472b6" stroke-width="1"/>
    <circle cx="16" cy="16" r="6" fill="#ffffff"/>
    <circle cx="16" cy="7" r="2.2" fill="#ffffff"/>
    <circle cx="16" cy="25" r="2.2" fill="#ffffff"/>
    <circle cx="24" cy="12" r="2.2" fill="#ffffff"/>
    <circle cx="8" cy="20" r="2.2" fill="#ffffff"/>
  `,
  AVAX: `
    <circle cx="16" cy="16" r="14.5" fill="#e84142" stroke="#fca5a5" stroke-width="1"/>
    <polygon points="16,8 9,23 13,23 16,16 19,23 23,23" fill="#ffffff"/>
  `,
  AVAXUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#e84142" stroke="#fca5a5" stroke-width="1"/>
    <polygon points="16,8 9,23 13,23 16,16 19,23 23,23" fill="#ffffff"/>
  `,
  LINK: `
    <circle cx="16" cy="16" r="14.5" fill="#375bd2" stroke="#93c5fd" stroke-width="1"/>
    <polygon points="16,7 23,11 23,21 16,25 9,21 9,11" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
  `,
  LINKUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#375bd2" stroke="#93c5fd" stroke-width="1"/>
    <polygon points="16,7 23,11 23,21 16,25 9,21 9,11" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
  `,
  TRX: `
    <circle cx="16" cy="16" r="14.5" fill="#eb0029" stroke="#fca5a5" stroke-width="1"/>
    <polygon points="16,7 24,14 16,25 8,14" fill="none" stroke="#ffffff" stroke-width="2"/>
    <line x1="8" y1="14" x2="24" y2="14" stroke="#ffffff" stroke-width="1.5"/>
    <line x1="16" y1="7" x2="16" y2="14" stroke="#ffffff" stroke-width="1.8"/>
  `,
  TRXUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#eb0029" stroke="#fca5a5" stroke-width="1"/>
    <polygon points="16,7 24,14 16,25 8,14" fill="none" stroke="#ffffff" stroke-width="2"/>
    <line x1="8" y1="14" x2="24" y2="14" stroke="#ffffff" stroke-width="1.5"/>
    <line x1="16" y1="7" x2="16" y2="14" stroke="#ffffff" stroke-width="1.8"/>
  `,
  ATOM: `
    <circle cx="16" cy="16" r="14.5" fill="#2e3148" stroke="#c084fc" stroke-width="1"/>
    <ellipse cx="16" cy="16" rx="9" ry="3.5" fill="none" stroke="#ba68c8" stroke-width="1.5" transform="rotate(30 16 16)"/>
    <ellipse cx="16" cy="16" rx="9" ry="3.5" fill="none" stroke="#64b5f6" stroke-width="1.5" transform="rotate(-30 16 16)"/>
    <circle cx="16" cy="16" r="2.5" fill="#ffffff"/>
  `,
  ATOMUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#2e3148" stroke="#c084fc" stroke-width="1"/>
    <ellipse cx="16" cy="16" rx="9" ry="3.5" fill="none" stroke="#ba68c8" stroke-width="1.5" transform="rotate(30 16 16)"/>
    <ellipse cx="16" cy="16" rx="9" ry="3.5" fill="none" stroke="#64b5f6" stroke-width="1.5" transform="rotate(-30 16 16)"/>
    <circle cx="16" cy="16" r="2.5" fill="#ffffff"/>
  `,
  DASH: `
    <circle cx="16" cy="16" r="14.5" fill="#008de4" stroke="#7dd3fc" stroke-width="1"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-style="italic" font-weight="900" font-size="16" fill="#ffffff">Đ</text>
  `,
  DASHUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#008de4" stroke="#7dd3fc" stroke-width="1"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-style="italic" font-weight="900" font-size="16" fill="#ffffff">Đ</text>
  `,
  AXS: `
    <circle cx="16" cy="16" r="14.5" fill="#0e1935" stroke="#22d3ee" stroke-width="1"/>
    <polygon points="16,7 23,16 16,25 9,16" fill="#00d8d5"/>
  `,
  AXSUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#0e1935" stroke="#22d3ee" stroke-width="1"/>
    <polygon points="16,7 23,16 16,25 9,16" fill="#00d8d5"/>
  `,
  ETC: `
    <circle cx="16" cy="16" r="14.5" fill="#059669" stroke="#6ee7b7" stroke-width="1"/>
    <polygon points="16,6 10.5,15.5 16,13 21.5,15.5" fill="#34d399"/>
    <polygon points="16,17.5 10.5,20 16,26 21.5,20" fill="#10b981"/>
  `,
  ETCUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#059669" stroke="#6ee7b7" stroke-width="1"/>
    <polygon points="16,6 10.5,15.5 16,13 21.5,15.5" fill="#34d399"/>
    <polygon points="16,17.5 10.5,20 16,26 21.5,20" fill="#10b981"/>
  `,
  ZEC: `
    <circle cx="16" cy="16" r="14.5" fill="#212529" stroke="#f4b728" stroke-width="1.2"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#f4b728">Z</text>
  `,
  ZECUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#212529" stroke="#f4b728" stroke-width="1.2"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#f4b728">Z</text>
  `,
  TRUMP: `
    <circle cx="16" cy="16" r="14.5" fill="#0f172a" stroke="#fbbf24" stroke-width="1.5"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#fbbf24">T</text>
  `,
  TRUMPUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#0f172a" stroke="#fbbf24" stroke-width="1.5"/>
    <text x="16" y="22" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="16" fill="#fbbf24">T</text>
  `,

  // Commodities
  GOLD: `
    <defs>
      <radialGradient id="goldGrad" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stop-color="#fff2a8"/>
        <stop offset="50%" stop-color="#eab308"/>
        <stop offset="100%" stop-color="#854d0e"/>
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14.5" fill="url(#goldGrad)" stroke="#fef08a" stroke-width="1"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#422006">Au</text>
  `,
  XAU: `
    <defs>
      <radialGradient id="xauGrad" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stop-color="#fff2a8"/>
        <stop offset="50%" stop-color="#eab308"/>
        <stop offset="100%" stop-color="#854d0e"/>
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14.5" fill="url(#xauGrad)" stroke="#fef08a" stroke-width="1"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#422006">Au</text>
  `,
  XAUUSD: `
    <defs>
      <radialGradient id="xauUsdGrad" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stop-color="#fff2a8"/>
        <stop offset="50%" stop-color="#eab308"/>
        <stop offset="100%" stop-color="#854d0e"/>
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14.5" fill="url(#xauUsdGrad)" stroke="#fef08a" stroke-width="1"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#422006">Au</text>
  `,
  XAG: `
    <defs>
      <radialGradient id="xagGrad" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="50%" stop-color="#cbd5e1"/>
        <stop offset="100%" stop-color="#475569"/>
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14.5" fill="url(#xagGrad)" stroke="#f8fafc" stroke-width="1"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#0f172a">Ag</text>
  `,
  XAGUSD: `
    <defs>
      <radialGradient id="xagUsdGrad" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="50%" stop-color="#cbd5e1"/>
        <stop offset="100%" stop-color="#475569"/>
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14.5" fill="url(#xagUsdGrad)" stroke="#f8fafc" stroke-width="1"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#0f172a">Ag</text>
  `,
  USOIL: `
    <circle cx="16" cy="16" r="14.5" fill="#1c1917" stroke="#f59e0b" stroke-width="1"/>
    <path d="M16 8 C16 8 10 15 10 19 C10 22.3 12.7 25 16 25 C19.3 25 22 22.3 22 19 C22 15 16 8 16 8 Z" fill="#f59e0b"/>
    <path d="M16 13 C16 13 12.5 17.5 12.5 20 C12.5 21.9 14.1 23.5 16 23.5 C17.9 23.5 19.5 21.9 19.5 20 C19.5 17.5 16 13 16 13 Z" fill="#fef08a"/>
  `,
  UKOIL: `
    <circle cx="16" cy="16" r="14.5" fill="#1c1917" stroke="#ea580c" stroke-width="1"/>
    <path d="M16 8 C16 8 10 15 10 19 C10 22.3 12.7 25 16 25 C19.3 25 22 22.3 22 19 C22 15 16 8 16 8 Z" fill="#ea580c"/>
    <path d="M16 13 C16 13 12.5 17.5 12.5 20 C12.5 21.9 14.1 23.5 16 23.5 C17.9 23.5 19.5 21.9 19.5 20 C19.5 17.5 16 13 16 13 Z" fill="#fed7aa"/>
  `,
  NGAS: `
    <circle cx="16" cy="16" r="14.5" fill="#083344" stroke="#06b6d4" stroke-width="1"/>
    <path d="M16 7 C16 7 10.5 14.5 10.5 19 C10.5 22.3 13 25 16 25 C19 25 21.5 22.3 21.5 19 C21.5 14.5 16 7 16 7 Z" fill="#06b6d4"/>
    <path d="M16 13 C16 13 12.8 17.5 12.8 20 C12.8 21.8 14.2 23.2 16 23.2 C17.8 23.2 19.2 21.8 19.2 20 C19.2 17.5 16 13 16 13 Z" fill="#cffafe"/>
  `,
  XPT: `
    <circle cx="16" cy="16" r="14.5" fill="#334155" stroke="#94a3b8" stroke-width="1.2"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#f1f5f9">Pt</text>
  `,
  XPTUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#334155" stroke="#94a3b8" stroke-width="1.2"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#f1f5f9">Pt</text>
  `,
  XPD: `
    <circle cx="16" cy="16" r="14.5" fill="#27272a" stroke="#a1a1aa" stroke-width="1.2"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#fafafa">Pd</text>
  `,
  XPDUSD: `
    <circle cx="16" cy="16" r="14.5" fill="#27272a" stroke="#a1a1aa" stroke-width="1.2"/>
    <text x="16" y="21" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="12" fill="#fafafa">Pd</text>
  `,

  // Stocks
  APPLE: `
    <circle cx="16" cy="16" r="14.5" fill="#18181b" stroke="#71717a" stroke-width="1"/>
    <path d="M15.5 10.5 C16 9.8 16.5 9 17.5 9 C17.6 10 17 10.8 16.5 11.4 C16 12 15.4 12.2 15.5 10.5 Z" fill="#ffffff"/>
    <path d="M19.2 16.8 C19.2 14.8 20.8 13.8 20.9 13.7 C19.9 12.3 18.4 12.1 17.9 12 C16.6 11.9 15.4 12.8 14.7 12.8 C14 12.8 13 12 12 12 C10.6 12 9.4 12.8 8.6 14.1 C7.2 16.6 8.2 20.2 9.6 22.2 C10.3 23.1 11.1 24.2 12.2 24.1 C13.2 24 13.6 23.4 14.8 23.4 C16 23.4 16.4 24.1 17.5 24.1 C18.6 24.1 19.3 23.1 20 22.1 C20.8 21 21.1 19.9 21.2 19.8 C21.1 19.7 19.2 19 19.2 16.8 Z" fill="#ffffff"/>
  `,
  AAPL: `
    <circle cx="16" cy="16" r="14.5" fill="#18181b" stroke="#71717a" stroke-width="1"/>
    <path d="M15.5 10.5 C16 9.8 16.5 9 17.5 9 C17.6 10 17 10.8 16.5 11.4 C16 12 15.4 12.2 15.5 10.5 Z" fill="#ffffff"/>
    <path d="M19.2 16.8 C19.2 14.8 20.8 13.8 20.9 13.7 C19.9 12.3 18.4 12.1 17.9 12 C16.6 11.9 15.4 12.8 14.7 12.8 C14 12.8 13 12 12 12 C10.6 12 9.4 12.8 8.6 14.1 C7.2 16.6 8.2 20.2 9.6 22.2 C10.3 23.1 11.1 24.2 12.2 24.1 C13.2 24 13.6 23.4 14.8 23.4 C16 23.4 16.4 24.1 17.5 24.1 C18.6 24.1 19.3 23.1 20 22.1 C20.8 21 21.1 19.9 21.2 19.8 C21.1 19.7 19.2 19 19.2 16.8 Z" fill="#ffffff"/>
  `,
  MSFT: `
    <circle cx="16" cy="16" r="14.5" fill="#0f172a" stroke="#334155" stroke-width="1"/>
    <rect x="10.5" y="10.5" width="5" height="5" fill="#f25022"/>
    <rect x="16.5" y="10.5" width="5" height="5" fill="#7fba00"/>
    <rect x="10.5" y="16.5" width="5" height="5" fill="#00a4ef"/>
    <rect x="16.5" y="16.5" width="5" height="5" fill="#ffb900"/>
  `,
  NVDA: `
    <circle cx="16" cy="16" r="14.5" fill="#000000" stroke="#76b900" stroke-width="1.2"/>
    <path d="M10 16 C10 12.7 12.7 10 16 10 C18.5 10 20.6 11.5 21.5 13.7 L19.2 14.8 C18.7 13.5 17.5 12.5 16 12.5 C14.1 12.5 12.5 14.1 12.5 16 C12.5 17.9 14.1 19.5 16 19.5 C17.5 19.5 18.7 18.5 19.2 17.2 L21.5 18.3 C20.6 20.5 18.5 22 16 22 C12.7 22 10 19.3 10 16 Z" fill="#76b900"/>
  `,
  TSLA: `
    <circle cx="16" cy="16" r="14.5" fill="#e82127" stroke="#fca5a5" stroke-width="1"/>
    <path d="M11 11 C13.5 10 18.5 10 21 11 L20 12.5 C18 11.8 14 11.8 12 12.5 Z M16 13.5 L17.5 14 L17.5 22 L14.5 22 L14.5 14 Z" fill="#ffffff"/>
  `,

  // Indices
  FTSE100: `
    <circle cx="16" cy="16" r="14.5" fill="#00247d" stroke="#60a5fa" stroke-width="1"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="9" fill="#ffffff">FTSE</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#93c5fd">100</text>
  `,
  ASX200: `
    <circle cx="16" cy="16" r="14.5" fill="#001f3f" stroke="#38bdf8" stroke-width="1"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="9.5" fill="#ffffff">ASX</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#7dd3fc">200</text>
  `,
  CAC40: `
    <circle cx="16" cy="16" r="14.5" fill="#002395" stroke="#ef4444" stroke-width="1.2"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="9.5" fill="#ffffff">CAC</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#fca5a5">40</text>
  `,
  CHINAA50: `
    <circle cx="16" cy="16" r="14.5" fill="#de2910" stroke="#fcd34d" stroke-width="1.2"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="9" fill="#ffffff">CHINA</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#fef08a">A50</text>
  `,
  EUROSTOXX50: `
    <circle cx="16" cy="16" r="14.5" fill="#003399" stroke="#facc15" stroke-width="1.2"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="8.5" fill="#ffffff">STOXX</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#fef08a">50</text>
  `,
  HK50: `
    <circle cx="16" cy="16" r="14.5" fill="#be123c" stroke="#f43f5e" stroke-width="1"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="10" fill="#ffffff">HK</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#fda4af">50</text>
  `,
  IBEX35: `
    <circle cx="16" cy="16" r="14.5" fill="#991b1b" stroke="#f59e0b" stroke-width="1"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="9" fill="#ffffff">IBEX</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#fde68a">35</text>
  `,
  NIKKEI225: `
    <circle cx="16" cy="16" r="14.5" fill="#ffffff" stroke="#bc002d" stroke-width="1.8"/>
    <text x="16" y="15.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="8" fill="#bc002d">NIKKEI</text>
    <text x="16" y="23" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="800" font-size="8" fill="#000000">225</text>
  `,
};

// Known currencies in the platform's Forex pairs
const KNOWN_CURRENCIES = new Set([
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD',
  'INR', 'PKR', 'BRL', 'MXN', 'TRY', 'ZAR', 'ARS', 'BDT',
  'COP', 'DZD', 'EGP', 'IDR', 'NGN', 'PHP', 'SGD', 'CNY',
  'HKD', 'SAR', 'AED', 'THB', 'KRW', 'PLN', 'SEK', 'NOK', 'DKK',
]);

let pairIconCounter = 0;

/**
 * Checks if a symbol represents a traditional Forex currency pair (6 letters like EURUSD, USDINR).
 */
export function isForexPair(symbol: string): boolean {
  const clean = symbol.replace(/_OTC$/, '').toUpperCase();
  if (clean.length !== 6) return false;
  // If explicitly registered as a single-asset brand (like BTCUSD, ETHUSD, XAUUSD), it's not a forex pair
  if (SINGLE_ASSET_SVGS[clean]) return false;
  const base = clean.slice(0, 3);
  const quote = clean.slice(3);
  return KNOWN_CURRENCIES.has(base) || KNOWN_CURRENCIES.has(quote);
}

/**
 * Renders an authoritative pure-vector pair icon for any instrument symbol.
 * Works seamlessly with .pair-svg (desktop) or .instrument-svg (mobile).
 * Guaranteed 100% resilient across WebKit (iOS Safari / Apple PWA) and Windows.
 */
export function renderPairIconSVG(symbol: string, options: { className?: string } = {}): string {
  const className = options.className || 'pair-svg';
  const clean = (symbol || '').replace(/_OTC$/, '').toUpperCase();

  // 1. Check if it's a known single-asset (Crypto, Commodity, Stock, Index)
  let singleAssetMarkup = SINGLE_ASSET_SVGS[clean];
  if (!singleAssetMarkup && clean.endsWith('USD')) {
    singleAssetMarkup = SINGLE_ASSET_SVGS[clean.slice(0, -3)];
  }

  if (singleAssetMarkup) {
    const idMap: Record<string, string> = {};
    const randomizedMarkup = singleAssetMarkup.replace(/id="([a-zA-Z0-9_-]+)"/g, (_, oldId) => {
      const newId = `${oldId}-${++pairIconCounter}`;
      idMap[oldId] = newId;
      return `id="${newId}"`;
    }).replace(/url\(#([a-zA-Z0-9_-]+)\)/g, (_, oldId) => {
      return `url(#${idMap[oldId] || oldId})`;
    });

    return `<svg class="${className}" viewBox="0 0 32 32" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">${randomizedMarkup}</svg>`;
  }

  // 2. Forex Currency Pair (Dual-Disc Vector Flags)
  if (clean.length === 6) {
    const base = clean.slice(0, 3);
    const quote = clean.slice(3);
    const baseVector = CURRENCY_VECTORS[base] || getFallbackCurrencyVector(base);
    const quoteVector = CURRENCY_VECTORS[quote] || getFallbackCurrencyVector(quote);
    const clip1 = `pfc1-${++pairIconCounter}`;
    const clip2 = `pfc2-${pairIconCounter}`;

    return `
      <svg class="${className}" viewBox="0 0 38 32" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="${clip1}"><circle cx="0" cy="0" r="12"/></clipPath>
          <clipPath id="${clip2}"><circle cx="0" cy="0" r="12"/></clipPath>
        </defs>
        <g transform="translate(13, 16)">
          <circle cx="0" cy="0" r="12" fill="#1e293b"/>
          <g clip-path="url(#${clip1})">${baseVector}</g>
          <circle cx="0" cy="0" r="12" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>
        </g>
        <g transform="translate(25, 16)">
          <circle cx="0" cy="0" r="13" fill="#0b0f19" stroke="#0b0f19" stroke-width="2"/>
          <g clip-path="url(#${clip2})">${quoteVector}</g>
          <circle cx="0" cy="0" r="12" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1"/>
        </g>
      </svg>
    `.replace(/\s+/g, ' ').trim();
  }

  // 3. Fallback Generic High-Contrast Badge for any unknown symbol
  const glyph = clean.slice(0, 3);
  return `
    <svg class="${className}" viewBox="0 0 32 32" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="14.5" fill="#182a3d" stroke="#38bdf8" stroke-width="1.2"/>
      <text x="16" y="20.5" text-anchor="middle" font-family="Inter,-apple-system,sans-serif" font-weight="900" font-size="10" fill="#38bdf8">${glyph}</text>
    </svg>
  `.replace(/\s+/g, ' ').trim();
}
